import { useEffect, useRef, useCallback } from 'react';
import { useAppStore } from '@/stores/appStore';
import { safeStorage } from '@/utils';
import type { WSMessage } from '@/types';

const RECONNECT_INTERVAL = 5000;
const MAX_RECONNECT_ATTEMPTS = 10;

export function useWebSocket() {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { setWsConnected, addWsMessage, addNotification, wsConnected } = useAppStore();

  const connect = useCallback(() => {
    const token = safeStorage.getItem('access_token');
    if (!token) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/ws?token=${token}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsConnected(true);
      reconnectAttemptsRef.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        // Backend sends { type, data, timestamp } — normalize to WSMessage { event, data, timestamp }
        const message: WSMessage = {
          event: msg.type || msg.event,
          data: msg.data || {},
          timestamp: msg.timestamp || new Date().toISOString(),
        };
        addWsMessage(message);

        // Convert important events to notifications
        const eventType = message.event;
        if (['trade_executed', 'risk_alert', 'risk_block', 'emergency_shutdown', 'price_update'].includes(eventType)) {
          if (eventType !== 'price_update') {
            addNotification({
              id: `ws-${Date.now()}`,
              type: eventType.includes('risk') ? 'risk' : 'trade',
              title: eventType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
              message: JSON.stringify(message.data).slice(0, 200),
              category: eventType.includes('risk') || eventType === 'emergency_shutdown' ? 'warning' : 'info',
              is_read: false,
              created_at: message.timestamp,
            });
          }
        }
      } catch {
        // Ignore malformed messages
      }
    };

    ws.onclose = () => {
      setWsConnected(false);
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectTimerRef.current = setTimeout(() => {
          reconnectAttemptsRef.current += 1;
          connect();
        }, RECONNECT_INTERVAL);
      }
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [setWsConnected, addWsMessage, addNotification]);

  const disconnect = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setWsConnected(false);
  }, [setWsConnected]);

  const send = useCallback((data: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  useEffect(() => {
    const token = safeStorage.getItem('access_token');
    if (token) {
      connect();
    }
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    wsConnected,
    send,
    disconnect,
    reconnect: connect,
  };
}
